import { Button, Col, Form, Input, Row, Select, Spin, Typography } from 'antd'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import routes from '@/constants/routes'
import { Customer, WashingMethod } from '@/types'
import { UpdateWashingMethodRequest } from '@/types/apis/washingMethods/UpdateWashingMethodRequest'

import { SelectWrapper } from '../../Customer/styles'
import { SpaceCustom } from '../../style'

type Props = {
  updateLoading: boolean
  getLoading?: boolean
  getWashingMethodResponse: WashingMethod | undefined
  customers: Customer[] | undefined
  onSubmit: (formValues: UpdateWashingMethodRequest) => void
}

type WashingMethodFormValues = {
  name: string
  time: number
  code: number
  volume: number
  cost: number
  waterName?: string
  quantity?: number
  waterQuantity: number
  detergents: {
    detergentName: string | undefined
    quantity: number | undefined
  }[]
}

type VolumeOptions = {
  value: number
  label: string
}

const { Title } = Typography

const UpdateWashingMethodForm = ({ updateLoading, getLoading, getWashingMethodResponse, onSubmit }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { id } = useParams()

  const [form] = Form.useForm<WashingMethodFormValues>()
  const volumeOptions: VolumeOptions[] = [
    { value: 100, label: '100' },
    { value: 75, label: '75' },
    { value: 50, label: '50' },
  ]

  const handleSubmit = (values: WashingMethodFormValues) => {
    const formattedPayload: UpdateWashingMethodRequest = {
      id: Number(id),
      name: values.name,
      time: Number(values.time),
      volume: Number(values.volume),
      code: Number(values.code),
      cost: Number(values.cost),
      waterQuantity: Number(values.waterQuantity),
      detergents: values.detergents.map((d) => ({
        id:
          getWashingMethodResponse?.washingMethodDetergents?.find((det) => det.detergent.name === d.detergentName)
            ?.id || 0,
        quantity: Number(d.quantity) || 0,
      })),
    }

    const waterExist = getWashingMethodResponse?.washingMethodDetergents.find(
      (washingMethodDetergent) => washingMethodDetergent.detergent.isWater
    )
    if (waterExist) {
      const waterObj = {
        id: waterExist.id,
        quantity: Number(values.quantity) || 0,
      }

      formattedPayload.detergents.push(waterObj)
    }

    onSubmit(formattedPayload)
  }

  useEffect(() => {
    if (getWashingMethodResponse) {
      const formattedDetergents = getWashingMethodResponse.washingMethodDetergents?.map((detergent) => ({
        detergentName: detergent?.detergent.name,
        quantity: detergent.quantity,
      }))

      const water = getWashingMethodResponse.washingMethodDetergents.find(
        (washingMethodDetergent) => washingMethodDetergent.detergent.isWater
      )
      if (water) {
        form.setFieldsValue({
          waterName: water.detergent.name,
          quantity: water.quantity,
        })
      }

      form.setFieldsValue({
        ...getWashingMethodResponse,
        detergents: formattedDetergents,
      })
    }
  }, [form, getWashingMethodResponse])

  return (
    <Spin spinning={getLoading}>
      <Form layout="vertical" onFinish={handleSubmit} form={form}>
        <Title level={5}>{t('components.pages.washingMethods.create.form.method')}</Title>

        <Form.Item
          label={t('components.pages.washingMethods.create.form.no')}
          name="code"
          rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireNo') }]}
        >
          <Input type="number" min={0} />
        </Form.Item>

        <Form.Item
          label={t('components.pages.washingMethods.create.form.name')}
          name="name"
          rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireName') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t('components.pages.washingMethods.create.form.time')}
          name="time"
          rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireTime') }]}
        >
          <Input type="number" min={0} suffix="分" />
        </Form.Item>

        <SelectWrapper>
          <Form.Item
            label={t('components.pages.washingMethods.create.form.volume')}
            name="volume"
            rules={[{ required: true, message: t('components.pages.tanks.create.form.requireVolume') }]}
          >
            <Select>
              {volumeOptions.map((volume) => (
                <Select.Option key={volume.value} value={volume.value}>
                  {volume.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </SelectWrapper>

        <Form.Item
          label={t('components.pages.washingMethods.create.form.cost')}
          name="cost"
          rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireCost') }]}
        >
          <Input type="number" min={0} suffix="円" />
        </Form.Item>

        <Form.Item
          label={t('components.pages.washingMethods.create.form.waterName')}
          name="waterQuantity"
          rules={[{ required: true, message: t('components.pages.washingMethods.create.form.requireQuantity') }]}
        >
          <Input type="number" min={0} suffix="l" />
        </Form.Item>

        <Title level={5}>{t('components.pages.washingMethods.create.form.detergentUsed')}</Title>
        <Form.List name="detergents">
          {(fields) => (
            <>
              {fields.map(({ key, name, ...restField }) => {
                return (
                  <Row key={key} gutter={16} align="middle">
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        label={t('components.pages.washingMethods.update.form.detergentName')}
                        name={[name, 'detergentName']}
                        rules={[
                          {
                            required: true,
                            message: t('components.pages.washingMethods.update.form.requireDetergentName'),
                          },
                        ]}
                      >
                        <Input disabled={true} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        label={t('components.pages.washingMethods.update.form.quantity')}
                        name={[name, 'quantity']}
                        rules={[
                          { required: true, message: t('components.pages.washingMethods.update.form.requireQuantity') },
                        ]}
                      >
                        <Input type="number" min={0} suffix="ml" />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              })}
            </>
          )}
        </Form.List>

        <Form.Item>
          <SpaceCustom>
            <Button onClick={() => navigate(routes.washingMethods.path)}>
              {t('components.pages.washingMethods.update.form.cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={updateLoading}>
              {t('components.pages.washingMethods.update.form.save')}
            </Button>
          </SpaceCustom>
        </Form.Item>
      </Form>
    </Spin>
  )
}

UpdateWashingMethodForm.displayName = 'UpdateWashingMethodForm'
export default UpdateWashingMethodForm
